import { useState, useRef, useEffect } from "react";
import { X, Search, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface Option {
  id: number;
  name: string;
}

interface MultiSelectTagsProps {
  options: Option[];
  selected: Option[];
  onChange: (selected: Option[]) => void;
  placeholder?: string;
  loading?: boolean;
}

export function MultiSelectTags({
  options,
  selected,
  onChange,
  placeholder = "Ara...",
  loading = false,
}: MultiSelectTagsProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedIds = new Set(selected.map((s) => s.id));

  const filtered = options.filter(
    (o) =>
      !selectedIds.has(o.id) &&
      o.name.toLocaleLowerCase("tr").includes(query.toLocaleLowerCase("tr"))
  );

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function addItem(item: Option) {
    onChange([...selected, item]);
    setQuery("");
  }

  function removeItem(id: number) {
    onChange(selected.filter((s) => s.id !== id));
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Selected tags */}
      {selected.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {selected.map((item) => (
            <span
              key={item.id}
              className="inline-flex items-center gap-1 rounded-md border border-accent/30 bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent"
            >
              {item.name}
              <button
                type="button"
                onClick={() => removeItem(item.id)}
                className="rounded-sm p-0.5 transition-colors hover:bg-accent/20"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="h-9 w-full rounded-lg border border-border bg-secondary pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        />
        {loading && (
          <Loader2 className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>

      {/* Dropdown */}
      {open && !loading && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-border/60 bg-card shadow-2xl shadow-black/50 ring-1 ring-white/5">
          {filtered.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => addItem(item)}
              className={cn(
                "w-full px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-secondary"
              )}
            >
              {item.name}
            </button>
          ))}
        </div>
      )}

      {open && !loading && query && filtered.length === 0 && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-border bg-card p-3 text-center text-sm text-muted-foreground shadow-lg">
          Sonuç bulunamadı
        </div>
      )}
    </div>
  );
}
